import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { Loader } from 'lucide-react'
import { useContext } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { getEnrollmentCoveragesQueryKey } from 'components/hooks/enrollments/useGetEnrollmentCoverages'
import { getGetBrokersUnprocessedEnrollmentsIdQueryKey } from 'utils/web_api'

import {
  type EnrollmentAction,
  type EnrollmentAutopayState,
  type EnrollmentStatus,
  type SubmissionMethod,
} from './types'
import { assertUnreachable, snakeToSentenceCase } from '../../utils'
import { useSendEnrollmentAction } from '../hooks/useSendEnrollmentAction'

function getAutopayActions (
  autopayState: EnrollmentAutopayState
): EnrollmentAction[] {
  const actions: EnrollmentAction[] = []
  // Both cases can trigger at once when autopay state is unspecified.
  if (autopayState !== 'on') {
    actions.push('mark_autopay_on')
  }
  if (autopayState !== 'off') {
    actions.push('mark_autopay_off')
  }
  return actions
}

function getCancelationAction (
  coverageStartDateString: string
): EnrollmentAction[] {
  const coverageStartDate = new Date(coverageStartDateString)
  const currentDate = new Date()

  // Below we standardize the time value within each datetime object so that
  // our comparison between the dates is correct. If the two dates are the same but
  // at different times, we still do not want to allow cancellation to occur.
  coverageStartDate.setHours(0, 0, 0, 0)
  currentDate.setHours(0, 0, 0, 0)

  if (coverageStartDate > currentDate) {
    return ['mark_canceled']
  } else {
    return []
  }
}

function getActionList (
  status: EnrollmentStatus | undefined,
  submissionMethod: SubmissionMethod | undefined,
  autopayState: EnrollmentAutopayState | undefined,
  coverageStartDate: string | undefined,
  hasEnrollmentPayments: boolean
): EnrollmentAction[] {
  // Override for enrollments that have been canceled but have enrollment payments.
  if (status === 'canceled' && hasEnrollmentPayments) {
    return ['mark_reinstated']
  }

  if (!status || !submissionMethod || !autopayState || !coverageStartDate) {
    return []
  }

  // Off-Thatch enrollments can only be canceled.
  if (submissionMethod === 'off_thatch') {
    return ['mark_canceled']
  }

  // If submission is automated, the only action is to switch to manual submission.
  if (submissionMethod !== 'manual') {
    switch (status) {
      case 'member_confirmed':
      case 'submission_processing':
      case 'carrier_processing':
        // If the enrollment is not done being submitted, ops can still mark it as
        // canceled.
        return [
          'switch_to_manual_submission',
          'mark_binder_paid',
          ...getAutopayActions(autopayState),
          'mark_canceled',
        ]
      default:
        return [
          'switch_to_manual_submission',
          'mark_binder_paid',
          ...getAutopayActions(autopayState),
        ]
    }
  }

  switch (status) {
    case 'member_confirmed':
      return ['begin_submission', 'mark_canceled']
    case 'manual_submission_in_progress':
      return [
        'mark_blocked',
        'mark_awaiting_general_agent',
        'mark_awaiting_member',
        'mark_completed',
        'mark_canceled',
      ]
    case 'manual_submission_awaiting_general_agent':
      return ['return_to_in_progress']
    case 'manual_submission_awaiting_member':
      return ['return_to_in_progress']
    case 'manual_submission_blocked':
      return ['return_to_in_progress']
    case 'binder_payment_required':
      return ['return_to_processing', 'mark_binder_paid']
    case 'autopay_setup_required': {
      const actions: EnrollmentAction[] = ['return_to_processing']
      return actions.concat(getAutopayActions(autopayState))
    }
    case 'completed': {
      const actions: EnrollmentAction[] = ['return_to_processing']
      actions.push(...getAutopayActions(autopayState))
      actions.push(...getCancelationAction(coverageStartDate))
      return actions
    }
    case 'in_member_cart':
      // This should not appear in the UI to a broker because we exclude in-cart
      // enrollments.
      return []
    case 'submission_processing':
      // This should not appear in the UI to a broker because for manual enrollments
      // we return the manual_submission_* statuses instead, and for automated enrollments
      // we generally skip this state.
      return []
    case 'carrier_processing':
      // This should not appear in the UI to a broker because it is not visited for
      // manual enrollments in today's state machine.
      return []
    case 'canceled':
      return []
    case undefined:
      return []
    default:
      assertUnreachable(status)
      return []
  }
}

export default function EnrollmentActionsPill ({
  enrollmentId,
  status,
  submissionMethod,
  autopayState,
  coverageStartDate,
  onError = () => {},
  hasEnrollmentPayments = false,
}: {
  enrollmentId: string
  status: EnrollmentStatus | undefined
  submissionMethod: SubmissionMethod | undefined
  autopayState: EnrollmentAutopayState | undefined
  coverageStartDate: string | undefined
  onError: () => void
  hasEnrollmentPayments: boolean
}) {
  const queryClient = useQueryClient()

  const { memberId } = useContext(BrokersMemberContext)

  const sendEnrollmentActionMutation = useSendEnrollmentAction({
    enrollmentId,
  })

  const actions = getActionList(
    status,
    submissionMethod,
    autopayState,
    coverageStartDate,
    hasEnrollmentPayments
  )

  if (actions.length === 0) {
    return
  }

  const handleDropdownClick =
    ({ action }: { action: EnrollmentAction }) =>
      async () => {
        if (action === 'mark_canceled') {
          if (
            !window.confirm('Are you sure you want to cancel this enrollment?')
          ) {
            return
          }
        }
        sendEnrollmentActionMutation.mutate(action, {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey:
              getGetBrokersUnprocessedEnrollmentsIdQueryKey(enrollmentId),
            })
            queryClient.invalidateQueries({
              queryKey:
              getGetBrokersUnprocessedEnrollmentsIdQueryKey(enrollmentId),
            })
            queryClient.invalidateQueries({
              queryKey: getEnrollmentCoveragesQueryKey({
                enrollmentId,
                memberId,
              }),
            })
          },
          onError: () => {
            onError()
          },
        })
      }

  return (
    <div>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <div
            aria-label='actions'
            className='inline-flex items-center rounded-full pl-4 pr-2 py-1 text-sm font-medium bg-gray-100 text-gray-800 whitespace-nowrap'
          >
            Actions
            {sendEnrollmentActionMutation.isPending
              ? (
                <Loader className='ml-2' size={16} />
                )
              : (
                <ChevronDownIcon className='ml-2' />
                )}
          </div>
        </DropdownMenu.Trigger>
        {!sendEnrollmentActionMutation.isPending && (
          <DropdownMenu.Portal>
            <DropdownMenu.Content className='DropdownMenuContent'>
              {actions.map((action) => (
                <DropdownMenu.Item
                  key={action}
                  className='DropdownMenuItem'
                  onClick={handleDropdownClick({ action })}
                >
                  {snakeToSentenceCase(action)}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        )}
      </DropdownMenu.Root>
    </div>
  )
}
